import { useId, useMemo, useRef, type ReactNode, type RefObject } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { CompactFigure, TileFailureStrip } from '@/components/dashboards/TargetTiles';
import {
  Button,
  ColumnManager,
  EmptyState,
  Panel,
  PanelHeader,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@/components/ui';
import { cx } from '@/lib/cx';
import {
  FUNNEL_METRICS,
  compactFigureParts,
  excludedByCurrencyNote,
  formatActualAmount,
  formatTargetAmount,
  targetMetricLabel,
  type FunnelMetric,
} from '@/lib/targets';
import type { ContributionRoll } from '@/lib/team-contribution';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import type { TeamSubject } from '@/lib/use-team-performance';
import {
  FIRST_METRIC,
  hasAnyFigure,
  panelDescription,
  pctText,
  silentMemberIds,
  teamLabel,
  type FigureMode,
  type MetricRolls,
} from './financial-shared';

/* -------------------------------------------------------------------------
 * THE COLUMNS — SHOW/HIDE, REORDER, FREEZE
 *
 * 🔴 **THIS TABLE IS `table-fixed` WITH A REAL `<colgroup>`, WHICH MAKES THE
 * COLUMN MANAGER A DIFFERENT PROBLEM HERE THAN ON THE LIST PAGES.** Under fixed
 * layout the `<col>` widths are AUTHORITATIVE and are matched to the header
 * cells BY POSITION — nothing reconciles them by name, and a `<col>` list one
 * entry longer or shorter than the header row shifts every column after the
 * discrepancy onto its neighbour's width. **Nothing errors, nothing overflows
 * and no assertion fails**: the table simply draws its figures under the wrong
 * headings, which is the failure this file already records for a heading wider
 * than its column.
 *
 * ⛔ **SO THE COLGROUP, THE HEADER AND EVERY BODY AND FOOTER ROW ARE DRAWN FROM
 * ONE ARRAY — `ColumnView.keys` — THROUGH THREE LOOPS AND NO OTHERS**
 * (`ColumnGroup`, `ContributionHead`, `MetricRow`). Each of the three runs the
 * SAME control flow: Person, else a metric, else nothing. A fourth loop written
 * against `FUNNEL_METRICS` would be correct until somebody hid a column.
 *
 * ⚠️ **PERSON IS NOT NECESSARILY FIRST.** It is locked, so it can neither be
 * hidden nor moved — but another column can be moved IN FRONT of it, so no loop
 * here may assume index 0 and no row may emit its label cell before the map.
 * --------------------------------------------------------------------- */

/**
 * The column that identifies the row.
 *
 * ⛔ **LOCKED, and on this table that is load-bearing twice over.** It names the
 * person a row of five figures belongs to, so hiding it leaves a grid of money
 * attributable to nobody — and every reconciliation row in the footer puts its
 * LABEL in this column, so without it "Team achieved", "Unaccounted" and the
 * rest become anonymous rows of figures under the member rows they are meant to
 * be distinguished from. It stays LISTED in the manager, disabled, so its
 * absence from that list cannot read as the feature having lost it.
 */
const PERSON_KEY = 'person';

/**
 * Every column this table renders, in its DECLARED order — what a reader who
 * has never opened the manager sees. `useColumnLayout` reorders and hides from
 * this list; it never adds to it, so a metric arriving on the funnel reaches
 * everybody rather than defaulting to hidden for whoever has stored a layout.
 *
 * ⚠️ **THE METRIC HALF IS `FUNNEL_METRICS`, NOT `TARGET_METRICS`** — the two
 * stopped being the same list on 2026-09-11, and the table's own header comment
 * says why it follows the funnel.
 */
const COLUMN_KEYS: readonly string[] = [PERSON_KEY, ...FUNNEL_METRICS];

const LOCKED_KEYS: readonly string[] = [PERSON_KEY];

/**
 * Why Person cannot be hidden, in the reader's terms.
 *
 * ⚠️ **THE MANAGER'S FALLBACK IS TRUE AND SAYS NOTHING** — *"This column cannot
 * be hidden"* — so the reason is supplied per key now that `ColumnManager`
 * takes one. It names the SECOND of the two arguments at {@link PERSON_KEY} as
 * well as the first, because the footer half is the one a reader cannot guess:
 * every reconciliation row puts its label in this column, so hiding it would
 * leave "Team achieved" and "Unaccounted" as anonymous rows of figures among
 * the member rows they exist to be distinguished from.
 *
 * ⚠️ **A why-this-is-disabled sentence, which the no-helper-text rule keeps by
 * name.** Answered per key rather than returned unconditionally, so a second
 * lock added here cannot inherit this one's reason.
 */
function lockReasonOf(key: string): string | undefined {
  return key === PERSON_KEY
    ? 'Every row and every total is labelled in this column. Without it the table is figures belonging to nobody.'
    : undefined;
}

/**
 * The stored scope.
 *
 * ⚠️ **One scope for a panel that renders on several teams.** Column layout is
 * the reader's own chrome, and a reader who hid `Pipeline` means it on whichever
 * team they look at next — keying this by subject would make the arrangement
 * reappear and vanish as they switch teams, which reads as the control not
 * having saved.
 */
const COLUMN_SCOPE = 'contribution';

/** The metric keys as a set, so a stale stored id can be recognised and dropped. */
const METRIC_KEYS: ReadonlySet<string> = new Set<string>(FUNNEL_METRICS);

/**
 * A column key narrowed to a metric, or `null`.
 *
 * ⚠️ **A KEY THIS TABLE NO LONGER HAS IS DROPPED, NEVER RENDERED.**
 * `applyOrder` already drops a stored key the table does not declare, so this
 * cannot fire today — it is the second half of the guard because
 * `useFrozenOffsets` indexes the header row's cells BY POSITION, so one cell
 * rendered for a key that is not in the measured list would shift every frozen
 * offset outboard of it by a whole column, silently.
 */
function asFunnelMetric(key: string): FunnelMetric | null {
  return METRIC_KEYS.has(key) ? (key as FunnelMetric) : null;
}

function isColumnKey(key: string): boolean {
  return key === PERSON_KEY || METRIC_KEYS.has(key);
}

/** The manager shows the same word the header does, from the same helper. */
function labelOf(key: string): string {
  if (key === PERSON_KEY) return 'Person';
  const metric = asFunnelMetric(key);
  return metric === null ? key : targetMetricLabel(metric);
}

/**
 * The metric columns' width — ONE number, spent twice.
 *
 * ⚠️ **The class and the figure are a pair and must move together.** The class
 * sizes the `<col>`; the figure is what the table's own minimum is summed from.
 * Tailwind scans SOURCE for class names, so the class cannot be built from the
 * number — and the number cannot be read back out of the class — which is
 * exactly why they sit on adjacent lines with this note between them.
 */
const METRIC_COL_CLASS = 'w-[9.5rem]';
const METRIC_COL_REM = 9.5;

/**
 * What is left for the Person column at the table's narrowest.
 *
 * ⚠️ **Person's `<col>` carries NO width, deliberately.** Under `table-fixed`
 * the sized columns take exactly what they ask for and the one unsized column
 * absorbs whatever remains, so Person grows with the viewport while every metric
 * column stays 9.5rem at any width. Give Person a width too and fixed layout
 * starts distributing slack proportionally across ALL of them, at which point
 * the metric columns are no longer a known constant.
 */
const PERSON_COL_MIN_REM = 10.5;

/** Which edge a column is frozen to, and how far in — see `Table.tsx`. */
interface FrozenProps {
  frozen?: 'left' | 'right';
  frozenOffset?: number;
}

/**
 * Everything the three loops need, in ONE object.
 *
 * ⚠️ **Threaded rather than recomputed per component.** `keys` is what the
 * offsets were MEASURED against, so a component that filtered its own copy
 * would be reading offsets indexed against a different list.
 */
interface ColumnView {
  /** Every visible column, in layout order — Person and metrics interleaved. */
  keys: readonly string[];
  /**
   * Just the metric columns, same order.
   *
   * ⚠️ **This is what the reconciliation predicates are asked about**, so a row
   * that would only have spoken through a hidden column stays hidden. Their own
   * comments say why.
   */
  metrics: readonly FunnelMetric[];
  frozenSideOf: (key: string) => 'left' | 'right' | undefined;
  offsets: Record<string, number>;
  /**
   * The table's minimum width, as an inline style.
   *
   * ⛔ **NEVER A CLASS.** Tailwind scans source text, so a computed
   * `min-w-[${n}rem]` emits no rule at all — and the class still lands in the
   * DOM, so it looks applied. `Table.tsx` gives the same answer for a frozen
   * column's offset.
   */
  minWidth: string;
  /**
   * The header row `useFrozenOffsets` measures, attached in `ContributionHead`.
   *
   * ⚠️ *SUPERSEDED: a `captionRef` callback that hung off the `<caption>` and
   * walked `closest('table')?.querySelector('thead tr')`, "A TYPE CONSTRAINT
   * RATHER THAN A PREFERENCE … `TRProps extends HTMLAttributes`, and React 19's
   * types declare `ref` on `RefAttributes` — which `HTMLAttributes` does not
   * extend — so `<TR ref={…}>` does not typecheck, and widening `Table.tsx` is
   * not this change's to make."* **`TRProps` declares `ref` now**, so the
   * indirection is gone and the ref sits on the element it refers to.
   *
   * ⚠️ **THE CAPTIONS STAYED, because they were never the ref.** They are the
   * table's accessible name and say what the figures are; only the `ref`
   * attribute came off them.
   *
   * ⚠️ **TWO MOUNTS SHARE THIS ONE REF** — the skeleton and the loaded table —
   * so the element under it is swapped when the data lands. `useFrozenOffsets`
   * tracks the observed row's identity itself and re-measures on the swap;
   * without that the offsets would hold the skeleton's widths for ever.
   */
  headRowRef: RefObject<HTMLTableRowElement | null>;
}

function frozenPropsFor(key: string, view: ColumnView): FrozenProps {
  const side = view.frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: view.offsets[key] ?? 0 };
}

/* -------------------------------------------------------------------------
 * WHICH RECONCILIATION ROWS HAVE ANYTHING TO SAY
 *
 * 🔴 **THE RULE, AND IT IS THE SAME ONE FOR ALL FOUR: A ROW IS HIDDEN WHILE IT
 * CARRIES NO INFORMATION AND IS NEVER DELETED.** Owner, 2026-09-11: *"remove
 * all the unusual data which is being shown"*. Every one of these rows was
 * built to expose a real discrepancy, and on production that day every one of
 * them was five em dashes or five zeros — so each is suppressed on the exact
 * condition that it is saying nothing, and **its reappearance is then itself
 * the finding**.
 *
 * ⚠️ **"IT IS ZERO" AND "IT DOES NOT EXIST" STAY DISTINGUISHABLE.** These
 * predicates are the only place the two are collapsed, and they are collapsed
 * in the direction of hiding a row rather than of printing one number for both.
 * Nothing below reads a figure through a `??` or a `Number(x) || 0`.
 *
 * 🔴 **EVERY ONE OF THEM IS ASKED ABOUT THE VISIBLE METRICS, NOT ABOUT
 * `FUNNEL_METRICS` — and that is the same rule, not a new one.** A row whose
 * only informative column the reader has hidden is a row saying nothing, which
 * is precisely what these predicates exist to remove; keeping it would put four
 * em dashes under the owner's *"remove all the unusual data which is being
 * shown"*. **Nothing is lost by it**: a finding in a hidden column is off screen
 * whether or not the row that would have carried it renders.
 *
 * ⚠️ **THE ROSTER IS NOT SCOPED THIS WAY AND MUST NOT BE.** `silentMemberIds`
 * and `hasAnyFigure` in `financial-shared` still read every funnel metric,
 * because "this person did nothing" and "anything at all was measured" are facts
 * about the DATA. Whether a column is on screen is a fact about the reader, and
 * letting it decide who counts as silent would collapse a member row by hiding
 * an unrelated column.
 * --------------------------------------------------------------------- */

/**
 * Whether ANY column has a team target to divide by.
 *
 * ⚠️ **It gates the "Team target" row AND the panel's own wording**, so the
 * table cannot be headed "share of the team target" over a denominator row that
 * is not there. Measured on production 2026-09-11: `f_revenue_targets` holds
 * five rows and every one belongs to a single USER, so no organisation target
 * and no group target exists and this is `false` on every team.
 *
 * 🔴 **ASKED ABOUT THE VISIBLE COLUMNS, WHICH IS WHAT KEEPS THE INVARIANT ABOVE
 * TRUE ONCE COLUMNS CAN BE HIDDEN.** Hiding the one metric that carries a team
 * target leaves a table that prints no target, so the heading must stop
 * promising one — asked about `FUNNEL_METRICS` instead, this panel would be
 * headed *"Contribution to the team target"* over a denominator row that is not
 * there, which is the exact state the owner had removed.
 */
function hasTeamTarget(rolls: MetricRolls, metrics: readonly FunnelMetric[]): boolean {
  return metrics.some((metric) => rolls[metric].basis.kind === 'team-target');
}

/**
 * Whether the members' sum says anything "Team achieved" has not already said.
 *
 * 🔴 **THE ROW EXISTS TO EXPOSE A MISMATCH, so its PRESENCE is the finding.**
 * On an `account_lead` team the two agree to the penny, which is the healthy
 * state and is five cells repeating the row above them.
 *
 * ⚠️ **A missing sum DIFFERS and must still be shown.** `membersTotal` is
 * `null` unless every member resolved — a sum missing two refused people is not
 * a smaller total, it is not a total — so a dash here is a real statement that
 * the reconciliation could not be made, and hiding it would make an unreadable
 * team look like a reconciled one.
 *
 * ⚠️ **Both `null` is AGREEMENT, not a difference.** Two dashes say the same
 * nothing, and printing one under the other is the noise this predicate exists
 * to remove.
 *
 * ⚠️ **The percentages need no separate test.** They are the same amounts over
 * one denominator, so equal amounts are equal shares; and where the basis
 * carries no denominator both sides are `null` together.
 */
function sumOfSharesDiffers(rolls: MetricRolls, metrics: readonly FunnelMetric[]): boolean {
  return metrics.some((metric) => {
    const { membersTotal, teamActual, residual } = rolls[metric];
    if (membersTotal === null && teamActual === null) return false;
    if (membersTotal === null || teamActual === null) return true;
    // Both known, so `residual` is their exact difference.
    return residual !== null && Number(residual) !== 0;
  });
}

/**
 * Whether the team's own figure holds anything no listed member accounts for.
 *
 * ⚠️ **`0.00` IS A MEASUREMENT AND IT IS THE ONE THAT SAYS NOTHING**: the
 * members reconcile, which is what an `account_lead` team is supposed to do.
 * `null` is *the residual could not be computed* and also renders nothing — the
 * two are not collapsed into one another anywhere, they are simply both
 * uninteresting, and `Number(null)` is never reached because the `!== null`
 * test comes first.
 */
function hasUnaccounted(rolls: MetricRolls, metrics: readonly FunnelMetric[]): boolean {
  return metrics.some((metric) => {
    const residual = rolls[metric].residual;
    return residual !== null && Number(residual) !== 0;
  });
}

/**
 * Whether any money is attributed to nobody.
 *
 * ⚠️ **Organisation subjects only — it is `null` on every group**, deliberately,
 * because a group total is already narrowed to its members. So this is
 * `false` on a group for a reason that is not "the figure is zero", and both
 * reach the same suppression. Measured on production 2026-09-11: the
 * organisation query answers `"0.00"` on every metric, which is a real
 * measurement saying every document has an account lead.
 *
 * ⚠️ **Suppressed on the SAME rule as "Unaccounted" right beside it**, which is
 * the point: two adjacent rows of five zeros, one hidden and one kept, would
 * read as the kept one meaning something.
 */
function hasUnattributed(
  byMetric: Partial<Record<FunnelMetric, string | null>>,
  metrics: readonly FunnelMetric[],
): boolean {
  return metrics.some((metric) => {
    const value = byMetric[metric] ?? null;
    return value !== null && Number(value) !== 0;
  });
}

/**
 * EACH MEMBER'S CONTRIBUTION, ACROSS EVERY FUNNEL METRIC AT ONCE.
 *
 * ⚠️ **There is no metric picker** — owner's instruction, 2026-09-02. The five
 * metrics are five columns, so nothing is one control away from being seen, and
 * `rollUpContributions` is called once per metric with its semantics per metric
 * unchanged.
 *
 * ⚠️ **THE DENOMINATOR IS ON SCREEN, as a figure rather than as a sentence.** A
 * bare percentage whose denominator is ambiguous is the thing people argue about
 * in meetings, and this one is genuinely not obvious: it is the TEAM's own
 * `targetAmount`, not the sum of the members' targets, and the two differ
 * whenever a group target is not the sum of its members' — which is the normal
 * case. `team-contribution.ts` records why that choice was made.
 *
 * ⚠️ **THE PERCENTAGES DO NOT SUM TO 100 AND THE TABLE SAYS SO IN FIGURES.**
 * With the team target as the denominator they sum to the team's own
 * ATTAINMENT, and the footer prints both so the two can be compared rather than
 * one being silently normalised into the other. Where they still disagree, the
 * difference is real money and `Unaccounted` names it.
 *
 * ── THE REDESIGN, AND WHERE THIS PANEL'S TWO CONTROLS WENT ─────────────────
 *
 * ⚠️ **THE MEMBER FILTER AND THE VALUES/PERCENTAGE SWITCH NOW LIVE IN THE TAB'S
 * SHARED FILTER BAR, behind its Advanced disclosure.** *SUPERSEDED: "THE TWO
 * CONTROLS BELOW STAYED ON THIS PANEL RATHER THAN JOINING THE TAB'S FILTER BAR,
 * and that is a correctness decision rather than a layout one."* Four analytical
 * tabs were each growing their own control strip and the owner's brief is that
 * they read as ONE dashboard, so the shape of the bar is decided once in
 * `DashboardFilterBar` rather than four times.
 *
 * 🔴 **THE HAZARD THAT ARGUMENT NAMED IS REAL AND IS NOT GONE — it is answered
 * on screen instead.** These two controls cannot touch the tiles, and up in the
 * bar they stand beside three that redraw them, which invites the reading that
 * the headline figures are narrowed to the chosen people. **They are not, and
 * neither is the reconciliation in this table's own footer, which still
 * describes the WHOLE team.** Two things say so where a reader is looking: the
 * bar renders a note naming how many of how many members are on screen the
 * moment anybody narrows, and the disclosure carries a count of the filters in
 * force even while it is shut. **Do not remove either and then move a control
 * here on the strength of this paragraph.**
 */
export function ContributionPanel({
  teamSubject,
  rolls,
  currency,
  memberFilter,
  figureMode,
  membersLoading,
  membersError,
  refused,
  unattributedByMetric,
  onOpenMemberMetric,
}: {
  teamSubject: TeamSubject;
  rolls: MetricRolls;
  /** The currency every money figure here is denominated in — see `actualsCurrency`. */
  currency: string;
  /**
   * The chosen member ids. **Empty is every member**, never none — the control
   * offers no "All members" row to tick, so empty is the only way to say "do
   * not narrow", and reading it as "show nobody" would blank the table the
   * moment somebody cleared their last chip.
   */
  memberFilter: readonly number[];
  figureMode: FigureMode;
  membersLoading: boolean;
  membersError: string | null;
  /*
   * ⚠️ **`membersHasMore` IS DELIBERATELY NOT TAKEN — 2026-09-05.** It drove one
   * thing: the strip saying the breakdown reads only the first 100 members,
   * which went with the rest of this app's helper text. `membersState.hasMore`
   * is still on the hook, so restoring a notice is a prop and a render, not a
   * refetch. ⚠️ **The truncation is real and is now unsaid**: a member list cut
   * short is indistinguishable from a member who contributed nothing.
   */
  refused: string | null;
  unattributedByMetric: Partial<Record<FunnelMetric, string | null>>;
  /**
   * Owner 2026-10-28 (member drill-down): when given, a MEMBER's real figure is
   * a button that calls this; the caller opens its one page-level modal with
   * that member's own `/targets/actuals` query. A refused, failed, loading or
   * unmeasurable cell stays inert, and so does every footer row — none of them
   * is one user's query. Absent → the table renders exactly as before.
   */
  onOpenMemberMetric?: (userId: number, name: string, metric: FunnelMetric) => void;
  /*
   * 🔴 **`excludedByMetric` WAS A PROP HERE AND IS NOT ANY MORE — 2026-09-11,
   * and the reasoning is kept because it still governs whoever adds it back.**
   *
   * *SUPERSEDED: "How many documents the currency filter dropped from the TEAM's
   * own figure, per metric.* ⚠️ *It is not on `ContributionRoll` and must not
   * be inferred from one. `rollUpContributions` carries the members' counts and
   * the team's money, and the team's exclusion is a fact about the team ROW —
   * summing the members' counts would double-count anybody whose documents were
   * also excluded and would report nothing at all on a team nobody could be read
   * for."*
   *
   * Every word of that is still true. What changed is that this panel had no
   * remaining use for it: its one reader was the duplicate note under "Team
   * achieved", and the team-level counts are carried once by
   * `FinancialAnalyticsPanel`'s own strip. **If a team-level exclusion is ever
   * wanted inside this table again, take it as a prop again — do NOT derive it
   * from the members.**
   */
}) {
  const headingId = useId();

  const layout = useColumnLayout(COLUMN_SCOPE, COLUMN_KEYS, LOCKED_KEYS);

  /*
   * ⚠️ **FILTERED, so the rendered cell count and the measured list are the same
   * list.** `applyOrder` already drops a stored key this table no longer
   * declares, so nothing should survive this filter — it is here because
   * `useFrozenOffsets` reads the header row's cells BY POSITION, and one
   * unmatched cell would silently shift every frozen offset outboard of it.
   *
   * ⚠️ **Memoised on `layout.visibleKeys`, which the hook already memoises**, so
   * the offsets hook is not handed a fresh array — and therefore a fresh
   * `ResizeObserver` — on every render of a panel that re-renders on every
   * filter change.
   */
  const columnKeys = useMemo(() => layout.visibleKeys.filter(isColumnKey), [layout.visibleKeys]);

  const visibleMetrics = useMemo(
    () => columnKeys.map(asFunnelMetric).filter((metric): metric is FunnelMetric => metric !== null),
    [columnKeys],
  );

  /*
   * 🔴 **THE OFFSETS ARE MEASURED, NOT COMPUTED FROM THE WIDTH CONSTANTS — and
   * on a `table-fixed` table that deserves saying, because computing them looks
   * available and is not.** The metric columns really are a known 9.5rem at
   * every viewport; **the PERSON column is the unsized one**, so its rendered
   * width is the table's width minus the metrics and is knowable only from the
   * DOM. Person is the natural freeze-left candidate and is first by default, so
   * the very first case anybody will hit — Person frozen, then a metric frozen
   * beside it — needs Person's width to place the second column. Arithmetic
   * would have been right for exactly the arrangements nobody makes.
   */
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  const offsets = useFrozenOffsets(headRowRef, columnKeys, layout.frozenSideOf);

  const view: ColumnView = {
    keys: columnKeys,
    metrics: visibleMetrics,
    frozenSideOf: layout.frozenSideOf,
    offsets,
    /*
     * ⚠️ **THE MINIMUM FOLLOWS THE VISIBLE SET.** Left at a literal `58rem` a
     * table with two columns hidden keeps scrolling sideways to nothing —
     * a horizontal scrollbar with empty space at the end of it, on a table that
     * would now have fitted. In `rem` because every `<col>` above is, so the two
     * cannot disagree about what a unit is.
     */
    minWidth: `${PERSON_COL_MIN_REM + visibleMetrics.length * METRIC_COL_REM}rem`,
    headRowRef,
  };

  /*
   * 🔴 **THE MEMBERS WHO CONTRIBUTED NOTHING AT ALL, COLLAPSED BY DEFAULT.**
   * Four of the account-lead team's members render `0.00` in all five columns
   * on production today, which is four rows of nothing in the middle of the
   * analytical half of this tab.
   *
   * ⚠️ **HIDDEN, NEVER DELETED, AND THE CONTROL SAYS HOW MANY.** *"Nobody on
   * this team contributed"* and *"the table only lists contributors"* are
   * different statements about a team, and a table that quietly dropped the
   * first would let a reader make the second. `silentMemberIds` is where the
   * line between a zero and a non-answer is drawn — a refusal, a failure, an
   * unmeasurable figure and a figure carrying a currency exclusion all stay on
   * screen whatever this switch says.
   *
   * ⚠️ **A VIEW CONTROL AND NOTHING ELSE.** It changes no request, no
   * arithmetic and no footer: the reconciliation below still reconciles the
   * whole team, including whoever is collapsed.
   */
  const [showSilent, setShowSilent] = useRememberedState('contribution-show-silent', false);
  const silentIds = silentMemberIds(rolls);
  /*
   * ⚠️ **Read ONCE and passed down, rather than recomputed in the table.** The
   * heading's wording and the denominator row are two statements about the same
   * fact, and a panel that called this twice could end up headed "share of the
   * team target" over a table with no such row.
   */
  const teamTargetExists = hasTeamTarget(rolls, visibleMetrics);
  /*
   * `admitted` is what the member filter alone leaves; `shown` is what this
   * panel is actually rendering. The difference is the number the switch below
   * offers to reveal — see `contributionRows` for why both come from one
   * function.
   */
  const admitted = contributionRows(rolls, memberFilter, silentIds, true);
  const shown = contributionRows(rolls, memberFilter, silentIds, showSilent);
  const hidden = admitted.length - shown.length;

  return (
    <Panel className="overflow-hidden">
      {/*
        * ⚠️ **WHAT IS COUNTED, WHOSE IT IS, AND IN WHAT UNIT — one line, and it
        * is a fact rather than helper text.** The denominator here is genuinely
        * not obvious (it is the TEAM's own target, not the sum of the members'),
        * so a heading reading "Contribution to the team target" over a grid of
        * percentages leaves a reader to infer the population and the unit.
        *
        * ⚠️ **"money in X", and only once something HAS been measured.** `Won
        * deals` is a COUNT, so a bare currency would put a unit on a figure that
        * has none; and naming a unit over a panel that measured nothing is a
        * claim about a currency nothing was summed in.
        */}
      {/*
        * 🔴 **THE TARGET LANGUAGE IS CONDITIONAL — owner, 2026-09-11.**
        *
        * *SUPERSEDED: `title="Contribution to the team target"` with the
        * description "Each member's share", unconditionally.* Measured on
        * production that day: every target in the system belongs to one USER,
        * so no team — organisation or group — has one, and this panel was
        * headed by a denominator that does not exist, over a "Team target" row
        * of five em dashes.
        *
        * ⚠️ **The wording and the denominator row are gated by ONE predicate**,
        * so the heading cannot promise a target the table does not print.
        */}
      <PanelHeader
        id={headingId}
        title={teamTargetExists ? 'Contribution to the team target' : 'Contribution by member'}
        description={panelDescription([
          teamTargetExists ? 'Each member’s share' : 'What each member closed',
          teamLabel(teamSubject),
          hasAnyFigure(rolls) ? `money in ${currency}` : null,
        ])}
        actions={
          /*
           * ⚠️ **THE MANAGER LIVES IN THIS PANEL'S OWN HEADER, not in the tab's
           * shared filter bar — and that is the opposite call from the member
           * filter and the figure switch two paragraphs above.** Those two
           * narrow the DATA and belong beside the controls that redraw the
           * tiles. This one rearranges ONE table's chrome and changes no
           * request, no arithmetic and no figure; up in the bar, beside three
           * controls that do move numbers, it would invite exactly the reading
           * the bar already has to carry a note to deny.
           *
           * ⚠️ **`PanelHeader`'s `actions` slot is `shrink-0` and cannot wrap**,
           * which rules out a BANK of controls here and is fine for one button —
           * the header stacks to a column below `sm`, so the trigger drops under
           * the title rather than crushing it.
           *
           * ⚠️ **The tab's analytics panel above is `sticky z-20`, a SIBLING of
           * this one.** This trigger scrolls with its own header rather than
           * being pinned under it, and the popover portals to rung 60, so it
           * draws over that panel rather than under it.
           */
          <ColumnManager
            layout={layout}
            labelOf={labelOf}
            lockedKeys={LOCKED_KEYS}
            lockReasonOf={lockReasonOf}
          />
        }
      />

      {membersError !== null && (
        <TileFailureStrip headline="Couldn’t read the team members." message={membersError} />
      )}

      {/*
        * 🔴 **THE AUDIT REFUSAL, SAID ONCE.**
        *
        * ⚠️ **`warning`, not `danger`: nothing is broken.** `/targets/actuals`
        * refuses a `subjectType: 'user'` query naming anybody but the caller
        * unless they hold the targets audit — so a manager who is not an
        * administrator gets the team's figures above and this instead of the
        * breakdown. Painting it as a failure would send somebody to fix a system
        * working exactly as configured.
        *
        * ⚠️ **The server's own sentence, and nothing invented beside it.** There
        * is no `targets` slug in this app's permission model, so nothing here can
        * pre-check the grant or name it more precisely than the server does.
        */}
      {refused !== null && (
        <TileFailureStrip
          headline="Only the team’s own figures are readable by your account."
          message={`${refused} The team totals above are unaffected.`}
          tone="warning"
        />
      )}

      {/*
        * ⚠️ A member list cut short is not a member who contributed nothing, and
        * on this tab that distinction is a statement about a person.
        */}
      {membersLoading ? (
        <MemberRowsSkeleton view={view} />
      ) : rolls[FIRST_METRIC].members.length === 0 ? (
        <EmptyState
          title={
            teamSubject.kind === 'organisation'
              ? 'No staff records to break this down by'
              : 'Nobody is in this team yet'
          }
          description={
            teamSubject.kind === 'organisation'
              ? 'The team totals above are still correct — there is simply nobody to attribute them to.'
              : 'Add people to this group from the Users list, and their share of the team target appears here.'
          }
        />
      ) : (
        <>
          <ContributionTable
            rolls={rolls}
            rows={shown}
            currency={currency}
            figureMode={figureMode}
            teamTargetExists={teamTargetExists}
            unattributedByMetric={unattributedByMetric}
            view={view}
            onOpenMemberMetric={onOpenMemberMetric}
          />

          {/*
            * ⚠️ **BELOW THE TABLE, where the hidden rows would have been.** A
            * reader scanning names to the end of the list meets the count
            * exactly where the people it names are missing from.
            *
            * ⚠️ **The number is how many rows this switch is removing RIGHT
            * NOW**, not how many members are silent in the team — somebody who
            * has already narrowed to two contributors is hiding nobody, and a
            * standing "4" beside an untouched table would be a fact about a
            * control rather than about what is on screen.
            */}
          {hidden > 0 && (
            <div className="border-t border-border px-4 py-2">
              <Button size="sm" variant="ghost" onClick={() => setShowSilent((prev) => !prev)}>
                {showSilent ? 'Hide' : 'Show'} {hidden.toLocaleString()}{' '}
                {hidden === 1 ? 'member' : 'members'} with no activity
              </Button>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

/* -------------------------------------------------------------------------
 * Which rows are on screen
 * --------------------------------------------------------------------- */

/** One member's row, and the index every metric's roll is read at. */
interface ContributionRow {
  member: ContributionRoll['members'][number];
  index: number;
}

/**
 * THE ROWS, DECIDED IN ONE PLACE.
 *
 * ⚠️ **One function rather than a predicate written out in the panel and again
 * in the table.** The panel needs the count of what it is hiding and the table
 * needs the rows themselves, and two copies of "which members are on screen"
 * is two things that can disagree — at which point a button offers to reveal
 * four people the table was never hiding.
 *
 * Every roll carries the same member list in the same order — they are built
 * from one `membersState` — so a row is identified by its INDEX and each cell
 * reads its own metric's roll at that index.
 */
function contributionRows(
  rolls: MetricRolls,
  memberFilter: readonly number[],
  silentIds: ReadonlySet<number>,
  showSilent: boolean,
): ContributionRow[] {
  return rolls[FIRST_METRIC].members
    .map((member, index) => ({ member, index }))
    /*
     * ⚠️ **An EMPTY selection is every member.** The control offers no "All
     * members" row to tick, so empty is the only way to say "do not narrow" —
     * and reading it as "show nobody" would blank the table the moment somebody
     * cleared their last chip.
     */
    .filter(({ member }) => memberFilter.length === 0 || memberFilter.includes(member.userId))
    .filter(({ member }) => showSilent || !silentIds.has(member.userId));
}

/**
 * ⚠️ **`min-w-[58rem]` HAS LEFT THIS CLASS AND IS NOW A SUM ON `ColumnView`.**
 * That literal was the width of five metric columns plus a person; with two
 * columns hidden it keeps the table scrolling sideways into empty space — a
 * horizontal scrollbar with nothing at the end of it, on a table that would now
 * have fitted. ⛔ **And it could not simply become `min-w-[${n}rem]`**: Tailwind
 * scans SOURCE text, so a computed arbitrary value emits no rule at all while
 * the class still lands in the DOM looking applied.
 *
 * ⚠️ **The sum now rides on `Table`'s own `style`**, which the primitive gained
 * for exactly this — so the class carries what is static and the style carries
 * what is computed, and neither has to pretend to be the other.
 */
const TABLE_CLASS = 'w-full table-fixed';

/**
 * The header row stays pinned inside a fixed box (owner 2026-10-18): this is
 * one panel among several on the tab, so it never claims the viewport.
 */
const FROZEN_MAX_HEIGHT = 'min(60vh, 560px)';

/*
 * ⚠️ *SUPERSEDED: a `TableMinWidth` wrapper that put the computed floor on a
 * plain `<div>` inside `TableWrap`'s scroller, "**`Table` takes `className` and
 * NOT `style`**, and widening a shared primitive is not this change's to
 * make".* **`Table` takes a `style` now**, so the floor goes on the element it
 * describes and the spacer is deleted. The rendered result is unchanged: the
 * table is `max(container, minWidth)` and `TableWrap` still owns the horizontal
 * scrolling.
 */

/**
 * 🔴 **LOOP 1 OF 3 OVER `view.keys` — and under `table-fixed` these `<col>`
 * widths are AUTHORITATIVE and are matched to the header cells BY POSITION.**
 * One entry more or fewer than the header row draws every following column at
 * its neighbour's width, with nothing erroring. Its control flow is deliberately
 * the same three branches as `ContributionHead` and `MetricRow`: Person, else a
 * metric, else nothing at all.
 *
 * ⚠️ **Person's `<col>` carries no width on purpose** — see `PERSON_COL_MIN_REM`
 * for why the unsized column is what keeps the metric columns a constant.
 */
function ColumnGroup({ view }: { view: ColumnView }) {
  return (
    <colgroup>
      {view.keys.map((key) => {
        if (key === PERSON_KEY) return <col key={key} />;
        if (asFunnelMetric(key) === null) return null;
        return <col key={key} className={METRIC_COL_CLASS} />;
      })}
    </colgroup>
  );
}

/**
 * 🔴 **LOOP 3 OF 3 OVER `view.keys` — every body AND footer row goes through
 * here.** Six near-identical switches (the member rows and the five
 * reconciliation rows) were six places for the rendered cells to drift out of
 * step with the `<colgroup>`; the label cell and the metric cell are the only
 * things that differ between them, so those are the props.
 *
 * ⚠️ **The label sits in the PERSON column wherever that column is**, rather
 * than being emitted before the map. Person is locked against hiding and moving,
 * but another column can be moved in FRONT of it — so a label written outside
 * the loop would sit in the wrong column the moment anybody reorders.
 */
function MetricRow({
  view,
  label,
  labelTitle,
  labelClassName,
  cellClassName,
  rowClassName,
  verticalAlign = 'top',
  cell,
}: {
  view: ColumnView;
  label: ReactNode;
  labelTitle?: string;
  labelClassName?: string;
  cellClassName?: string;
  rowClassName?: string;
  verticalAlign?: 'middle' | 'top';
  cell: (metric: FunnelMetric) => ReactNode;
}) {
  return (
    <TR className={rowClassName}>
      {view.keys.map((key) => {
        const frozen = frozenPropsFor(key, view);
        if (key === PERSON_KEY) {
          return (
            <TD
              key={key}
              verticalAlign={verticalAlign}
              className={labelClassName}
              {...(labelTitle === undefined ? {} : { title: labelTitle })}
              {...frozen}
            >
              {label}
            </TD>
          );
        }
        const metric = asFunnelMetric(key);
        if (metric === null) return null;
        return (
          <TD key={key} numeric verticalAlign={verticalAlign} className={cellClassName} {...frozen}>
            {cell(metric)}
          </TD>
        );
      })}
    </TR>
  );
}

function MemberRowsSkeleton({ view }: { view: ColumnView }) {
  return (
    // Same frozen box as the loaded table, so nothing re-flows when rows arrive.
    <TableWrap frozenHeader frozenMaxHeight={FROZEN_MAX_HEIGHT}>
      <Table className={TABLE_CLASS} style={{ minWidth: view.minWidth }}>
        <caption className="sr-only">Loading the team breakdown.</caption>
        {/*
          * ⚠️ **The skeleton carries the `<colgroup>` TOO, which it did not
          * before.** Without one, `table-fixed` divides the width equally, so
          * the loading columns sat at different widths from the loaded ones and
          * every column jumped as the data arrived.
          */}
        <ColumnGroup view={view} />
        <ContributionHead view={view} />
        <TBody>
          {Array.from({ length: 4 }, (_, i) => (
            <MetricRow
              key={i}
              view={view}
              verticalAlign="middle"
              label={<Skeleton className="h-3 w-40" />}
              cell={() => <Skeleton className="ml-auto h-3 w-20" />}
            />
          ))}
        </TBody>
      </Table>
    </TableWrap>
  );
}

/**
 * ⚠️ **One column per metric, in the READER's order and narrowed to what they
 * have kept.** *SUPERSEDED: "in `FUNNEL_METRICS` order".* The DECLARED order is
 * still `FUNNEL_METRICS`, read from `@/lib/targets` rather than written out here,
 * so a metric arriving on the funnel grows this table instead of being silently
 * absent from it — the same reason `COLUMN_COUNT` on the deliverables tracker is
 * derived and not a literal. What `useColumnLayout` does to that list can only
 * ever REORDER or SUBTRACT, never add, which is why a new metric still reaches
 * everybody including readers who have stored a layout.
 *
 * ⚠️ **`FUNNEL_METRICS` AND NOT `TARGET_METRICS` SINCE 2026-09-11**, and the two
 * are no longer the same list: `invoiced` can be measured and not targeted,
 * `booked` the reverse. This table reports on what the funnel above it shows, so
 * it follows the funnel — a column for a metric with no tile would be a figure
 * with no headline to reconcile against.
 *
 * ⚠️ **`wrap` on the metric headings.** Under `table-fixed` a heading wider than
 * its `<col>` does not truncate and does not widen the column — it paints over
 * its neighbour, legibly enough that the row reads as one long word rather than
 * as a fault. `Won deals` and `Collected` fit today; the columns are narrow
 * enough that a renamed metric would not.
 *
 * 🔴 **LOOP 2 OF 3 OVER `view.keys`.** It runs the same three branches as
 * `ColumnGroup` and `MetricRow`, and it is the row `useFrozenOffsets` MEASURES —
 * so the cells it renders are the list the offsets are indexed against, and the
 * `<colgroup>` above it is drawn from the very same array.
 */
function ContributionHead({ view }: { view: ColumnView }) {
  return (
    <THead>
      {/*
        * ⚠️ **THE REF SITS HERE NOW**, on the row the hook measures, rather than
        * on the `<caption>` that used to stand in for it — see
        * `ColumnView.headRowRef`.
        */}
      <TR ref={view.headRowRef}>
        {view.keys.map((key) => {
          const frozen = frozenPropsFor(key, view);
          if (key === PERSON_KEY) {
            return (
              <TH key={key} {...frozen}>
                Person
              </TH>
            );
          }
          const metric = asFunnelMetric(key);
          if (metric === null) return null;
          return (
            <TH key={key} numeric wrap {...frozen}>
              {targetMetricLabel(metric)}
            </TH>
          );
        })}
      </TR>
    </THead>
  );
}

/**
 * The meter for one person's share. **Geometry only.**
 *
 * ⚠️ **Clamped at BOTH ends.** An unclamped low end emits `width: -25%`, which
 * is invalid CSS: the browser drops the declaration and the block-level inner
 * element falls back to `auto`, which inside this track is the FULL track. A
 * loss would render as a completely full bar. Same guard, and the same reason,
 * as `AttainmentBar` — and it is written out rather than shared because that one
 * draws attainment against a target and this draws a share of a team, so the two
 * are only coincidentally alike.
 */
function ShareBar({ pct }: { pct: number | null }) {
  if (pct === null) return null;
  const negative = pct < 0;
  return (
    <div aria-hidden="true" className="mt-1 h-1.5 overflow-hidden rounded-xs bg-surface-sunken">
      <div
        className={cx('h-full rounded-xs', negative ? 'bg-danger' : 'bg-fg-secondary')}
        style={{ width: negative ? '0.375rem' : `${Math.min(pct, 100)}%` }}
      />
    </div>
  );
}

/**
 * What a figure left out, under the figure it qualifies.
 *
 * 🔴 **RENDERED AGAIN AS OF 2026-09-11 — the GBP-only ruling that silenced it is
 * withdrawn.** A figure that silently omits 234 paid invoices is the defect this
 * whole change exists to fix, so where an actual is rendered this belongs beside
 * it.
 *
 * ⚠️ **The QUIET register, never `warning` and never `danger`.** Nothing is
 * broken: the exclusion is what the server measured and reported honestly. A
 * coloured band would send somebody to fix a system doing exactly what it says.
 */
function ExcludedLine({ excluded }: { excluded: number | null }) {
  const note = excludedByCurrencyNote(excluded);
  if (note === null) return null;
  return (
    <span className="mt-0.5 block text-2xs text-fg-subtle" title={note.detail}>
      {note.short}
    </span>
  );
}

/**
 * An ACTUAL amount, compacted — `£20.00K`, with the unit in gold.
 *
 * 🔴 **IT REACHES THE FOOT'S TWO TOTALS NOW — owner, 2026-09-17: *"make these
 * figures as K and M and if less than 1000 then show the actual value"*.**
 * *SUPERSEDED: "A MEMBER's amount … **COMPACT IN THE BODY, LONG FORM IN THE FOOT
 * — owner's decision, 2026-09-13, and the boundary is argued at the `<tfoot>`
 * rather than here.**"* The boundary is still argued at the `<tfoot>` and it has
 * MOVED: it now runs between the totals and the GAP figures rather than between
 * the body and the foot. Read that block before changing either side.
 *
 * ⚠️ **"Team achieved" and "Sum of the shares above" are ACTUALS, exactly as a
 * member's amount is**, which is why they take this component rather than a
 * second one — the team's figure and the members' figures are the same quantity
 * at two scales, and a register that changed between them would read as two
 * different kinds of number stacked on one another.
 *
 * This component exists so the two figure MODES cannot drift: the same member
 * amount is the headline under Values and the sub-line under Percentage, and a
 * compaction applied at one of those two call sites and not the other would make
 * a figure change shape when a reader touched a control that changes no request.
 *
 * ⚠️ **`compactFigureParts` IS THE METRIC-AWARE ONE AND THAT IS LOAD-BEARING ON
 * THIS TABLE.** `won_deals` is a column here — it is on `FUNNEL_METRICS` — and it
 * is a COUNT. That function drops the currency symbol and the `K`/`M` unit BY THE
 * METRIC, so a count renders `40` with `unit: ''` and `CompactFigure` emits no
 * gold span at all. **A metric-free money formatter would put a `£` on a deal
 * count**, which is the reading this table must never offer.
 *
 * ⚠️ **THE EXACT DECIMAL SURVIVES ON THE `title`, and that is the only place it
 * survives.** `1.23M` has thrown away everything below the ten thousand, so the
 * long form is not decoration — it is the figure. Every call site passes it.
 */
function CompactActual({
  metric,
  amount,
  currency,
  className,
}: {
  metric: FunnelMetric;
  /** The exact decimal string off the roll — never a pre-formatted figure. */
  amount: string;
  currency: string;
  className?: string;
}) {
  return (
    <span className={className} title={formatActualAmount(metric, amount, currency)}>
      <CompactFigure parts={compactFigureParts(metric, amount, currency, 'actual')} />
    </span>
  );
}

/**
 * The TEAM TARGET, compacted — the denominator every share is divided by.
 *
 * ⚠️ **A SIBLING RATHER THAN A `role` PROP ON {@link CompactActual}, and
 * `targets.ts` sets the precedent**: `formatTargetAmount` and
 * `formatActualAmount` are two thin wrappers over one function for exactly this
 * reason. The role cannot then be passed wrongly at a call site — it is stated
 * by which component is written — and each of the two carries an honest doc
 * instead of one doc hedging between two facts.
 *
 * ⚠️ **`FigureRole` decides nothing TODAY and that is not a reason to fold the
 * two together.** Under the GBP-only rule `formatFigureAmount` ignores it; it is
 * threaded through `compactFigureParts` and is a shipped part of that signature,
 * and the two roles have already meant different things once (a money TARGET
 * with no currency is a broken row; a money ACTUAL with none is the ordinary
 * empty state). **Right answer, inert reason — the pair is what survives the
 * reason coming back.**
 *
 * ⚠️ **Compacted although it is NOT part of the reconciliation subtraction**, and
 * that is the point: it is read as a pair with "Team achieved" directly beneath
 * it, so a long-form target over a compacted achieved would put two registers on
 * the one pair a reader compares. It is conditional on a team target EXISTING,
 * never on a DIFFERENCE, so nothing about it can round a stated discrepancy away.
 */
function CompactTarget({
  metric,
  amount,
  currency,
}: {
  metric: FunnelMetric;
  /** The exact decimal string off the basis — never a pre-formatted figure. */
  amount: string;
  currency: string;
}) {
  return (
    <span title={formatTargetAmount(metric, amount, currency)}>
      <CompactFigure parts={compactFigureParts(metric, amount, currency, 'target')} />
    </span>
  );
}

/**
 * One member's figure for one metric.
 *
 * ⚠️ **FOUR OUTCOMES, FOUR DIFFERENT CELLS, AND NONE OF THEM IS A ZERO.** A
 * refusal, a failure, an unmeasurable figure and a real figure of zero are
 * different facts about a person, and the three that are not measurements must
 * never look like one.
 *
 * ⚠️ **THE 2026-09-13 COMPACTION TOUCHED ONLY THE FOURTH.** The three branches
 * above are unchanged to the character: a compaction is a rule about how a
 * PRESENT figure is printed, and it has nothing to say about a figure that is
 * refused, failed or unmeasurable. Read a diff here that alters any of the first
 * three as a mistake.
 */
function MemberCell({
  member,
  metric,
  mode,
  currency,
  onOpen,
}: {
  member: ContributionRoll['members'][number];
  metric: FunnelMetric;
  mode: FigureMode;
  currency: string;
  /** Opens this member's drill-down; honoured only on a real figure. */
  onOpen?: () => void;
}) {
  if (member.status === 'refused') return <span className="text-xs text-warning">Not readable</span>;
  if (member.status === 'error') return <span className="text-xs text-danger">Couldn’t read</span>;
  if (member.actual === null) return <span className="text-fg-subtle">—</span>;

  /*
   * The member drill-down (owner 2026-10-28): only a SUCCESSFUL read carries a
   * query to reuse, so a loading row stays inert like the three branches above.
   * The headline figure becomes the button; the sub-lines and the bar stay
   * outside it, exactly as `TileFigures` keeps its target line outside.
   */
  const openable = onOpen !== undefined && member.status === 'ok';
  const exact = formatActualAmount(metric, member.actual, currency);
  const asButton = (content: ReactNode) =>
    openable ? (
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open ${targetMetricLabel(metric)} details for ${member.name}: ${exact}`}
        className={DRILL_BUTTON_CLASS}
      >
        {content}
      </button>
    ) : (
      content
    );

  /*
   * ⚠️ **`pct === null` IS NOT ZERO PER CENT, AND PERCENTAGE MODE MUST NOT
   * INVENT ONE.** A member has no share whenever the team's basis carries no
   * denominator — no target set, or a basis that cannot be compared — and that
   * is the ordinary case on a metric nobody has targeted. `pctText` prints an em
   * dash for it, so the headline degrades to "no figure" while the amount below
   * still says what the person actually did. The amount is never withheld on
   * that branch: it is the only real measurement in the cell.
   */
  if (mode === 'percentage') {
    return (
      <>
        {/*
          * ⚠️ **THE HEADLINE'S `title` IS THE AMOUNT AND STAYS THE LONG FORM.**
          * It was already carrying it before the compaction, which is the trap
          * in this branch: the amount is BOTH a `title` up here and a rendered
          * figure below, so compacting the shared variable rather than the
          * render site would have put an abbreviated number in the one place
          * kept for the exact one, and the precise figure would exist nowhere
          * in the cell. `formatActualAmount` is called here rather than hoisted
          * above the branch because the Values arm no longer reads it —
          * `CompactActual` builds its own `title` — and this cell renders up to
          * five times per member per render.
          */}
        {asButton(<span title={exact}>{pctText(member.pct)}</span>)}
        {/*
          * ⚠️ **ITS OWN `title`, because a `title` on a SIBLING does not cover
          * it.** The headline above and this line are siblings inside a
          * fragment, not ancestor and descendant, so hovering the compacted
          * figure would otherwise answer nothing — on the one element in the
          * cell whose precision was actually dropped. `CompactActual` supplies
          * it, so no call site can forget.
          */}
        <CompactActual
          metric={metric}
          amount={member.actual}
          currency={currency}
          className="mt-0.5 block text-2xs text-fg-subtle"
        />
        <ExcludedLine excluded={member.excludedByCurrency} />
        <ShareBar pct={member.pct} />
      </>
    );
  }

  return (
    <>
      {asButton(<CompactActual metric={metric} amount={member.actual} currency={currency} />)}
      {member.pct !== null && (
        <span className="mt-0.5 block text-2xs text-fg-subtle">{pctText(member.pct)} of target</span>
      )}
      <ExcludedLine excluded={member.excludedByCurrency} />
      <ShareBar pct={member.pct} />
    </>
  );
}

/**
 * The clickable-figure affordance, the same classes `TileFigures` puts on a
 * tile's figure button (minus the tile's own type size), so a member's figure
 * and a team tile read as the same kind of control.
 */
const DRILL_BUTTON_CLASS =
  'cursor-pointer rounded-sm border-0 bg-transparent p-0 text-left underline-offset-4 decoration-hairline-strong hover:underline focus-visible:underline';

/** The dash a cell shows when there is no figure. **Never a zero.** */
function NoCellFigure() {
  return <span className="text-fg-subtle">—</span>;
}

function ContributionTable({
  rolls,
  rows,
  currency,
  figureMode,
  teamTargetExists,
  unattributedByMetric,
  view,
  onOpenMemberMetric,
}: {
  rolls: MetricRolls;
  /** Exactly the rows to render, already narrowed — see `contributionRows`. */
  rows: readonly ContributionRow[];
  currency: string;
  figureMode: FigureMode;
  /** `hasTeamTarget`, computed by the panel so the heading and this agree. */
  teamTargetExists: boolean;
  unattributedByMetric: Partial<Record<FunnelMetric, string | null>>;
  /** The visible columns, their order, their freezes — see `ColumnView`. */
  view: ColumnView;
  /** See the prop on `ContributionPanel`. Member rows only, never the footer. */
  onOpenMemberMetric?: (userId: number, name: string, metric: FunnelMetric) => void;
}) {
  return (
    <TableWrap frozenHeader frozenMaxHeight={FROZEN_MAX_HEIGHT}>
      <Table className={TABLE_CLASS} style={{ minWidth: view.minWidth }}>
        <caption className="sr-only">
          {teamTargetExists
            ? 'Each team member’s share of the team target, by metric.'
            : 'What each team member closed, by metric. No team target is set.'}
        </caption>
        <ColumnGroup view={view} />
        <ContributionHead view={view} />
        <TBody>
          {/*
            * ⚠️ **AN EMPTY BODY IS SAID, NOT LEFT BLANK.** Two things can empty
            * it and both are states of a control rather than of the business: a
            * member filter naming nobody on this team (which is what switching
            * team leaves behind), and every remaining member being collapsed as
            * silent. A head and a reconciliation footer with nothing between
            * them reads as a failed render, and the reconciliation underneath
            * would then be the only figures on screen — describing a team whose
            * rows are all hidden.
            *
            * ⚠️ **THE SPAN IS THE LIVE COLUMN COUNT.** *SUPERSEDED:
            * `1 + FUNNEL_METRICS.length`.* One cell spanning more columns than
            * the table has stretches the row past the `<colgroup>` under
            * `table-fixed`; one spanning fewer leaves the sentence in a narrow
            * box with empty cells beside it. Neither errors.
            */}
          {rows.length === 0 && (
            <TR>
              <TD colSpan={view.keys.length} className="text-fg-muted">
                No members are shown. Clear the filters above, or reveal the members with no
                activity.
              </TD>
            </TR>
          )}
          {rows.map(({ member, index }) => (
            /*
             * ⚠️ A hover tint written out rather than `interactive`, which is
             * named for CLICKABLE rows. The ROW is not clickable — only each
             * figure is (owner 2026-10-28, its own button); the tint is for
             * tracking one person across six columns of a table wider than
             * the viewport, which is a reading aid and not an affordance.
             */
            <MetricRow
              key={member.userId}
              view={view}
              rowClassName="transition-colors duration-150 ease-out hover:bg-surface-hover motion-reduce:transition-none"
              label={member.name}
              labelTitle={member.name}
              labelClassName="truncate font-medium text-fg"
              cellClassName="tabular"
              cell={(metric) => {
                const row = rolls[metric].members[index];
                return row === undefined ? (
                  <NoCellFigure />
                ) : (
                  <MemberCell
                    member={row}
                    metric={metric}
                    mode={figureMode}
                    currency={currency}
                    onOpen={
                      onOpenMemberMetric === undefined
                        ? undefined
                        : () => onOpenMemberMetric(row.userId, row.name, metric)
                    }
                  />
                );
              }}
            />
          ))}
        </TBody>

        {/*
          * ⚠️ **THE RECONCILIATION, AND IT IS THE HONEST HALF OF THIS TABLE.**
          * The members' shares sum to the TEAM'S ATTAINMENT, not to 100% — so
          * both are printed, side by side, rather than one being normalised into
          * the other. Two further rows say where the difference went:
          *
          *   Not attributed to anyone   documents whose account lead resolves to
          *                              nobody. Reported by the server for the
          *                              ORGANISATION only, so it is `null` on a
          *                              group and renders as nothing — never as a
          *                              zero, which would be a positive claim the
          *                              query never made.
          *
          *   Unaccounted                the team's own figure that no listed
          *                              member's figure covers. `null` unless
          *                              EVERY member resolved — a sum missing two
          *                              refused people is not a smaller total, it
          *                              is not a total, and a residual computed
          *                              from one would be the people we could not
          *                              read wearing the name of missing money.
          *
          * ⚠️ **`bg-surface-sunken` matches `THead`, so the head and the foot
          * BRACKET the member rows.** Five reconciliation rows in body type read
          * as five more people; the tint is what separates the summary from the
          * data, and it is the only change the redesign made to this block.
          *
          * ── 🔴 THE TOTALS COMPACT. THE GAP FIGURES DO NOT. ───────────────────
          *
          * Owner, 2026-09-17: *"make these figures as K and M and if less than
          * 1000 then show the actual value"*, reported against this foot — the
          * member rows above it were already reading `£5.56K` / `£1.11M` while
          * `Team achieved 43,145.34 GBP` sat underneath them.
          *
          * ⚠️ *SUPERSEDED: "**EVERY FIGURE IN THIS FOOT IS LONG FORM.
          * DELIBERATE.** … **SO THE WHOLE FOOT IS UNIFORM, and that is not
          * timidity** … **'Team target' and 'Team achieved' are long form for the
          * SAME reason even though neither is conditional**, because splitting
          * the foot is the one arrangement with no defence."* The foot IS split
          * now, and the line is drawn one row lower than that argument allowed
          * for:
          *
          *     Team target              COMPACT   a denominator, not a difference
          *     Team achieved            COMPACT   an actual, like the rows above
          *     Sum of the shares above  COMPACT   an actual, like the rows above
          *     Not attributed to anyone  EXACT    a GAP figure
          *     Unaccounted               EXACT    a GAP figure
          *
          * 🔴 **THE HAZARD THE OLD ARGUMENT NAMED IS REAL, IS NOT GONE, AND IS
          * WHY THE BOTTOM TWO ROWS DID NOT MOVE.** It is worth restating rather
          * than deleting, because it is what the split is built around. *"Sum of
          * the shares above"* renders ONLY when it disagrees with *"Team
          * achieved"* (`sumOfSharesDiffers`, and this block's own note below says
          * **its PRESENCE is the finding**). Compacted, a difference below the
          * printed resolution leaves two identical-looking figures stacked above
          * a row asserting they differ — and at `M` that resolution is £10,000,
          * which a residual is routinely smaller than. The owner's own screenshot
          * is exactly that case: `2,124,230.51` against `2,124,230.50`, both
          * `£2.12M` once compacted.
          *
          * ✅ **WHAT ANSWERS IT IS STRUCTURAL, NOT A HOPE — the explaining row is
          * GUARANTEED to be on screen in every state where compaction could hide
          * the difference, and it prints the difference EXACTLY.** Read the two
          * predicates together: `sumOfSharesDiffers`' third arm is
          * `residual !== null && Number(residual) !== 0`, which is `hasUnaccounted`
          * **character for character, on the same metric**. So whenever that row
          * appears because two KNOWN figures differ, *"Unaccounted"* appears with
          * it. Its other arm — exactly one of the two being `null` — renders a
          * dash against a figure, which is visibly different at any precision and
          * needs no explaining row at all.
          *
          *     Team achieved            £2.12M     ← hover: 2,124,230.51 GBP
          *     Sum of the shares above  £2.12M     ← hover: 2,124,230.50 GBP
          *     Unaccounted              0.01 GBP   ← exact, always
          *
          * ⛔ **SO THE TWO GAP ROWS ARE THE HONESTY ARTEFACT AND MUST NEVER BE
          * COMPACTED, at any magnitude.** They are the very quantity being
          * reported as missing, so a rounding error in one of them is the same
          * order as the thing it reports — `CLAUDE.md` states this as a standing
          * rule for gap figures across the dashboards, and this is its sharpest
          * instance because these two also carry the conditional render. **Both**
          * stay exact, not just the residual: two adjacent gap rows in two
          * registers would read as meaning different kinds of thing, which is the
          * same reasoning that keeps them suppressed or shown together.
          *
          * ⚠️ **THE EXACT FIGURE SURVIVES ON EVERY COMPACTED CELL'S `title`** —
          * `CompactActual` and `CompactTarget` both supply it, so no call site
          * here can forget one. That is what makes the top three rows lossy in
          * DISPLAY only: the precise number is one hover away, where before this
          * change a compacted total would have put it nowhere on screen.
          *
          * ⚠️ **THE ARITHMETIC IS UNTOUCHED AND MUST STAY UNTOUCHED.**
          * `sumOfSharesDiffers`, `hasUnaccounted` and `hasUnattributed` all test
          * the EXACT decimal strings off the roll; compaction happens at the
          * render site and nowhere else. ⛔ **A conditional render and a lossy
          * format must never be applied to the same number** — the condition
          * stays on the exact value, which is precisely why the bottom two rows
          * can be trusted to appear.
          *
          * ⚠️ **Width is not the reason and must not become it.** These cells are
          * `w-[9.5rem]` and were rendering the long form comfortably before this,
          * so nothing here was compacted to fit. If a metric column is ever
          * narrowed, PAGE the decision back to the owner rather than compacting
          * the gap rows — the arithmetic is the point of the block.
          */}
        <tfoot className="border-t border-hairline-strong bg-surface-sunken">
          {/*
            * ⚠️ **THE DENOMINATOR, AS A FIGURE, PER COLUMN.** A bare percentage
            * whose denominator is ambiguous is the thing people argue about in
            * meetings, and this one is genuinely not obvious: it is the TEAM's own
            * `targetAmount`, not the sum of the members' targets, and the two
            * differ whenever a group target is not the sum of its members' — which
            * is the normal case.
            */}
          {/*
            * ⚠️ **HIDDEN WHILE NO COLUMN HAS A TEAM TARGET — 2026-09-11.** With
            * none set it was a whole row of em dashes labelled as the thing
            * every percentage is divided by, which reads as a broken lookup
            * rather than as an absent commitment. **It returns the moment one
            * target exists**, and it returns for ALL columns then, because a
            * denominator present on some metrics and dashed on others is
            * exactly the discrepancy this row is for.
            *
            * ⚠️ **"No column" now means no VISIBLE column** — see
            * `hasTeamTarget`, which the panel asks about the columns on screen
            * so this row and the heading above it cannot disagree.
            */}
          {teamTargetExists && (
            <MetricRow
              view={view}
              label="Team target — every share is divided by this"
              labelClassName="font-medium text-fg"
              cellClassName="tabular font-medium text-fg"
              cell={(metric) => {
                const { basis } = rolls[metric];
                return basis.kind === 'team-target' ? (
                  <CompactTarget metric={metric} amount={basis.denominator} currency={currency} />
                ) : (
                  <NoCellFigure />
                );
              }}
            />
          )}
          <MetricRow
            view={view}
            label="Team achieved"
            labelClassName="font-medium text-fg"
            cellClassName="tabular font-medium text-fg"
            cell={(metric) => {
              const roll = rolls[metric];
              return (
                <>
                  {/*
                    * ⚠️ `null` is "cannot be measured", never zero — the whole
                    * reason `actual` was widened. A dash here, and the reason for
                    * it in the strip above this table.
                    */}
                  {roll.teamActual === null ? (
                    <NoCellFigure />
                  ) : (
                    <CompactActual metric={metric} amount={roll.teamActual} currency={currency} />
                  )}
                  {roll.teamPct !== null && (
                    <span className="mt-0.5 block text-2xs font-normal text-fg-muted">
                      {pctText(roll.teamPct)} of target
                    </span>
                  )}
                  {/*
                    * 🔴 **THE TEAM'S EXCLUSION COUNT IS NOT REPEATED HERE —
                    * 2026-09-11.** *SUPERSEDED: an `ExcludedLine` reading the
                    * same `excludedByMetric` prop, under the same figures.* It
                    * is the SAME set of counts the strip under the tiles
                    * already carries, from the same source, so it was the one
                    * exclusion note on this tab that was a literal second copy
                    * rather than a new fact.
                    *
                    * ⚠️ **NOTHING WAS LOST AND NOTHING WAS SILENCED.** The
                    * team-level counts are still on screen, once, above; the
                    * PER-MEMBER counts in the cells above this row stay,
                    * because each is a fact about one person that no strip can
                    * express — and `silentMemberIds` keeps a member visible on
                    * the strength of one, so removing the note would leave a
                    * zero row with nothing explaining why it is there.
                    *
                    * ⚠️ **THE PROP WENT WITH IT.** A prop threaded through two
                    * components and read by neither is the shape this repo
                    * already names *an export nothing imports*. The per-member
                    * notes above read `member.excludedByCurrency` off the roll
                    * and never this, so they are untouched.
                    */}
                </>
              );
            }}
          />
          {/*
            * 🔴 **SHOWN ONLY WHERE IT DISAGREES WITH "Team achieved" — the row
            * exists to expose that disagreement, so its PRESENCE is the
            * finding.** On production 2026-09-11 it repeated the row above it
            * to the penny in every column, which is the healthy state of an
            * `account_lead` team and a row of nothing to read.
            *
            * ⚠️ **A sum that could not be MADE also counts as a disagreement**
            * and brings the row back as a dash — see `sumOfSharesDiffers`.
            * `membersTotal` is `null` unless every member resolved, so hiding
            * that case would make a team we could not read look reconciled.
            *
            * ⚠️ **Asked about the VISIBLE columns**, so a disagreement in a
            * column the reader has hidden no longer brings back a row that
            * would repeat "Team achieved" in every column they can see.
            */}
          {sumOfSharesDiffers(rolls, view.metrics) && (
            <MetricRow
              view={view}
              label="Sum of the shares above"
              cellClassName="tabular"
              cell={(metric) => {
                const roll = rolls[metric];
                return (
                  <>
                    {roll.membersTotal === null ? (
                      <NoCellFigure />
                    ) : (
                      <CompactActual
                        metric={metric}
                        amount={roll.membersTotal}
                        currency={currency}
                      />
                    )}
                    <span className="mt-0.5 block text-2xs font-medium text-fg-muted">
                      {pctText(roll.membersPctTotal)}
                    </span>
                  </>
                );
              }}
            />
          )}
          {/*
            * ⚠️ **NON-ZERO ONLY, on the same rule as "Unaccounted" below it.**
            * *SUPERSEDED: shown whenever the server reported the figure at all.*
            * It is reported for the ORGANISATION alone, and on production
            * 2026-09-11 it answered `"0.00"` on every metric — a real
            * measurement meaning every document has an account lead, and a row
            * that will read zero for as long as that stays true.
            *
            * ⚠️ **Suppressing "Unaccounted" and keeping this would be worse
            * than keeping both**: two adjacent rows of zeros, one hidden, makes
            * the survivor look like it means something.
            *
            * ⛔ **LONG FORM, WHILE EVERY ROW ABOVE IT COMPACTS — NOT AN
            * OVERSIGHT.** It is a GAP figure: money attributed to nobody, which
            * is the quantity the row exists to report, so a rounding error in it
            * is the same order as its own subject. The same reasoning that keeps
            * it and "Unaccounted" suppressed together keeps them in one register
            * together. See the `<tfoot>` note.
            */}
          {hasUnattributed(unattributedByMetric, view.metrics) && (
            <MetricRow
              view={view}
              label="Not attributed to anyone"
              labelClassName="text-fg-muted"
              cellClassName="tabular text-fg-muted"
              cell={(metric) => {
                const value = unattributedByMetric[metric] ?? null;
                return value === null ? '' : formatActualAmount(metric, value, currency);
              }}
            />
          )}
          {/*
            * ⚠️ **NON-ZERO ONLY — 2026-09-11.** *SUPERSEDED: shown whenever a
            * residual could be COMPUTED.* `0.00` is the answer an
            * `account_lead` team is supposed to give: the members reconcile to
            * the team figure exactly. The row is for the day they do not, and
            * on that day it comes back naming the money.
            *
            * ⛔ **LONG FORM, AND THIS IS THE ONE CELL IN THE FOOT THAT CANNOT
            * MOVE.** The two rows above it compact, so this row is the only
            * place their difference is readable — compact it too and a residual
            * below the printed resolution would leave three rows agreeing that
            * something is missing and no figure anywhere saying how much. It is
            * exact at EVERY magnitude, not merely below 1000. See the `<tfoot>`
            * note for the guarantee that it is on screen whenever the rows above
            * it could look identical.
            */}
          {hasUnaccounted(rolls, view.metrics) && (
            <MetricRow
              view={view}
              label="Unaccounted"
              labelClassName="text-fg-muted"
              cellClassName="tabular text-fg-muted"
              cell={(metric) => {
                const residual = rolls[metric].residual;
                return residual === null ? '' : formatActualAmount(metric, residual, currency);
              }}
            />
          )}
        </tfoot>
      </Table>
    </TableWrap>
  );
}
